import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  PageHeader,
  Skeleton,
  Stack,
} from '@mailflow/ui';
import { api } from '../lib/api.js';
import { percent } from '../lib/analytics.js';
import { Info } from '../components/Info.js';
import { SenderList } from '../components/SenderList.js';
import { WebhookSetup } from '../components/WebhookSetup.js';
import { SendingPolicyList } from '../components/SendingPolicyList.js';

interface Check {
  key: 'spf' | 'dkim' | 'dmarc';
  status: 'ok' | 'warn' | 'fail' | 'missing';
  summary: string;
  advice: string[];
  suggestion?: { name: string; type: string; value: string };
  found?: string;
}

interface DomainReport {
  domain: string;
  selector: string;
  checks: Check[];
  status: Check['status'];
  dnsbl: Array<{ name: string; zone: string; listed: boolean; codes: string[]; error?: string }>;
  sendingIp: string | null;
  checkedAt: string;
}

const STATUS_LABELS: Record<string, string> = {
  ok: 'in Ordnung',
  warn: 'verbesserungswürdig',
  fail: 'fehlerhaft',
  missing: 'fehlt',
};

const CHECK_LABELS: Record<string, string> = {
  spf: 'SPF — wer darf in deinem Namen senden',
  dkim: 'DKIM — Signatur der Nachricht',
  dmarc: 'DMARC — was bei Fehlschlag geschehen soll',
};

export function DeliverabilityPage() {
  const [ip, setIp] = useState('');
  const [applied, setApplied] = useState('');

  const report = useQuery({
    queryKey: ['deliverability', applied],
    queryFn: ({ signal }) =>
      api.get<{ domains: DomainReport[]; hint?: string }>(
        `/api/deliverability${applied ? `?ip=${encodeURIComponent(applied)}` : ''}`,
        signal,
      ),
    // DNS-Antworten ändern sich nicht im Sekundentakt, und jede Abfrage
    // kostet Wartezeit.
    staleTime: 300_000,
  });

  return (
    <Stack gap={6}>
      <PageHeader
        title="Zustellbarkeit"
        description="Ob deine Mails ankommen, entscheidet sich hier — nicht im Text. Seit 2024 verlangen Gmail und Outlook von Massenversendern eine nachweisbare Absenderkennung; ohne sie landet auch eine gute Mail im Spam-Ordner."
      />

      <ProjectDomainSettings />

      <Card
        title="Domain prüfen"
        description="Die Prüfung fragt öffentliche DNS-Einträge ab und verändert nichts."
      >
        <form
          className="inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            setApplied(ip.trim());
          }}
        >
          <input
            className="input"
            value={ip}
            onChange={(e) => setIp(e.target.value)}
            placeholder="Versand-IP (optional) — prüft zusätzlich die Blocklisten"
            aria-label="Versand-IP"
          />
          <Button type="submit" loading={report.isFetching}>
            Prüfen
          </Button>
        </form>
      </Card>

      {report.data?.hint && <Alert tone="info">{report.data.hint}</Alert>}

      {report.isLoading && (
        <Card>
          <Skeleton lines={4} label="DNS wird abgefragt" />
        </Card>
      )}

      {report.isError && (
        <ErrorState
          title="Die DNS-Prüfung ist fehlgeschlagen"
          error={report.error}
          hint="Das heißt nicht, dass deine Einträge fehlerhaft sind — nur, dass sie gerade nicht abgefragt werden konnten. Versuche es erneut."
          action={
            <Button variant="secondary" onClick={() => void report.refetch()}>
              Erneut prüfen
            </Button>
          }
        />
      )}

      {report.data?.domains.map((domain) => (
        <DomainCard key={domain.domain} report={domain} />
      ))}

      <SenderList />

      <SendingPolicyList />

      <WebhookSetup />

      <SpamPreview />
      <DmarcReports />
    </Stack>
  );
}

function ProjectDomainSettings() {
  const queryClient = useQueryClient();
  const [domainInput, setDomainInput] = useState<string | null>(null);

  const projectQuery = useQuery({
    queryKey: ['project'],
    queryFn: ({ signal }) =>
      api.get<{ project: { id: string; name: string; domain: string } }>('/api/project', signal),
  });

  const updateDomain = useMutation({
    mutationFn: (domain: string) =>
      api.patch<{ project: { id: string; name: string; domain: string } }>('/api/project', {
        domain,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['project'] });
      void queryClient.invalidateQueries({ queryKey: ['deliverability'] });
      setDomainInput(null);
    },
  });

  const currentDomain = projectQuery.data?.project.domain ?? '';
  const isPlaceholder =
    !currentDomain || currentDomain === 'example.com' || currentDomain === 'localhost';
  const effectiveValue = domainInput ?? currentDomain;

  return (
    <Card
      title="Projektdomain & Absender-Einrichtung"
      description="Lege die Hauptdomain für dein Projekt fest. Das Zustellbarkeits-Center prüft deine öffentlichen DNS-Einträge (SPF, DKIM, DMARC) gegen diese Domain."
    >
      <Stack gap={4}>
        {isPlaceholder && (
          <Alert tone="warning">
            Aktuell ist noch der Platzhalter <strong>example.com</strong> eingetragen. Trage hier deine tatsächliche Versand-Domain (z. B. <code>meinedomain.ch</code>) ein, um die Zustellbarkeit deiner E-Mails zu aktivieren und zu prüfen.
          </Alert>
        )}

        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            const val = effectiveValue.trim();
            if (val) updateDomain.mutate(val);
          }}
        >
          <input
            className="input"
            style={{ minWidth: '18rem' }}
            value={effectiveValue}
            onChange={(e) => setDomainInput(e.target.value)}
            placeholder="z. B. meinedomain.ch"
            aria-label="Projektdomain"
          />
          <Button
            type="submit"
            loading={updateDomain.isPending}
            disabled={!effectiveValue.trim() || effectiveValue.trim() === currentDomain}
          >
            Domain speichern
          </Button>
        </form>

        {updateDomain.isError && (
          <p className="text-sm" data-tone="danger">
            {updateDomain.error instanceof Error
              ? updateDomain.error.message
              : 'Speichern fehlgeschlagen.'}
          </p>
        )}

        {updateDomain.isSuccess && (
          <p className="text-sm" data-tone="success">
            Projektdomain erfolgreich aktualisiert!
          </p>
        )}
      </Stack>
    </Card>
  );
}

/** Ordnet den Prüfstatus einem Tonfall der Marke zu. */
const STATUS_TONE: Record<Check['status'], 'success' | 'warning' | 'danger'> = {
  ok: 'success',
  warn: 'warning',
  fail: 'danger',
  missing: 'danger',
};

/** Der Info-Knopf je Prüfung — SPF, DKIM und DMARC sagen einem Neuling nichts. */
const CHECK_INFO: Record<Check['key'], 'spf' | 'dkim' | 'dmarc'> = {
  spf: 'spf',
  dkim: 'dkim',
  dmarc: 'dmarc',
};

function DomainCard({ report }: { report: DomainReport }) {
  return (
    <Card
      title={report.domain}
      actions={<Badge tone={STATUS_TONE[report.status]}>{STATUS_LABELS[report.status]}</Badge>}
    >
      <Stack gap={4}>
        {report.checks.map((check) => (
          <article key={check.key} className="check" data-status={check.status}>
            <header className="check__head">
              <strong>{CHECK_LABELS[check.key]}</strong>
              <Info zu={CHECK_INFO[check.key]} />
              <Badge tone={STATUS_TONE[check.status]}>{STATUS_LABELS[check.status]}</Badge>
            </header>
            <p className="text-sm">{check.summary}</p>

            {check.found && <code className="check__record">{check.found}</code>}

            {check.advice.length > 0 && (
              <ul className="check__advice text-sm">
                {check.advice.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}

            {check.suggestion && <CopyRecord suggestion={check.suggestion} />}
          </article>
        ))}

        {report.dnsbl.length > 0 && (
          <Stack gap={2}>
            <h3 className="check__head" style={{ fontSize: 'var(--text-base)' }}>
              <span>Blocklisten für {report.sendingIp}</span>
              <Info zu="dnsbl" />
            </h3>
            <ul className="check__advice text-sm">
              {report.dnsbl.map((entry) => (
                <li key={entry.zone}>
                  <strong>{entry.name}:</strong>{' '}
                  {entry.error ? (
                    // Eine fehlgeschlagene Abfrage ist kein sauberes Ergebnis.
                    <span data-tone="warning">{entry.error}</span>
                  ) : entry.listed ? (
                    <span data-tone="danger">gelistet ({entry.codes.join(', ')})</span>
                  ) : (
                    'nicht gelistet'
                  )}
                </li>
              ))}
            </ul>
          </Stack>
        )}

        <p className="muted text-sm">
          Geprüft am {new Date(report.checkedAt).toLocaleString('de-CH')}. DNS-Änderungen brauchen
          je nach TTL bis zu einem Tag, bis sie überall sichtbar sind.
        </p>
      </Stack>
    </Card>
  );
}

function CopyRecord({ suggestion }: { suggestion: { name: string; type: string; value: string } }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(suggestion.value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Ohne Zugriff auf die Zwischenablage bleibt der Wert lesbar daneben
      // stehen — er ist nicht verloren, nur nicht kopiert.
      setCopied(false);
    }
  }

  return (
    <div className="suggestion">
      <div>
        <span className="field__label">
          {suggestion.type}-Eintrag auf <code>{suggestion.name}</code>
        </span>
        <code className="check__record">{suggestion.value}</code>
      </div>
      <Button variant="secondary" onClick={copy}>
        {copied ? 'Kopiert' : 'Kopieren'}
      </Button>
    </div>
  );
}

// --- Spam-Vorschau ----------------------------------------------------------

interface SpamResult {
  findings: Array<{
    code: string;
    severity: 'info' | 'warn' | 'error';
    message: string;
    reason: string;
  }>;
  stats: {
    textLength: number;
    imageCount: number;
    linkCount: number;
    textToImageRatio: number | null;
    subjectLength: number;
    capsRatio: number;
  };
  verdict: 'gut' | 'verbesserungswuerdig' | 'riskant';
}

const VERDICTS: Record<string, string> = {
  gut: 'unauffällig',
  verbesserungswuerdig: 'verbesserungswürdig',
  riskant: 'riskant',
};

function SpamPreview() {
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [text, setText] = useState('');

  const check = useMutation({
    mutationFn: () =>
      api.post<SpamResult>('/api/deliverability/spam-preview', {
        subject,
        html,
        text,
        hasUnsubscribe: true,
      }),
  });

  return (
    <section className="card">
      <div className="card__body stack">
        <h2 style={{ fontSize: 'var(--text-lg)' }}>Spam-Vorschau</h2>
        {/*
          Bewusst kein nachgebauter Punktestand: eine Zahl wie „4,7" sagt
          nicht, was zu ändern wäre.
        */}
        <p className="muted text-sm">
          Kein Punktestand, sondern konkrete Befunde mit Begründung. Geprüft wird nur, was sich am
          Inhalt feststellen lässt — Reputation und Empfängerhistorie stehen hier nicht drin.
        </p>

        <Field label="Betreff" value={subject} onChange={(e) => setSubject(e.target.value)} />

        <div className="field">
          <label className="field__label" htmlFor="spam-html">
            HTML-Fassung
          </label>
          <textarea
            id="spam-html"
            className="input"
            rows={6}
            value={html}
            onChange={(e) => setHtml(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="spam-text">
            Textfassung
          </label>
          <textarea
            id="spam-text"
            className="input"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>

        <div>
          <Button onClick={() => check.mutate()} loading={check.isPending}>
            Prüfen
          </Button>
        </div>

        {check.data && (
          <>
            <p>
              Urteil:{' '}
              <span className="badge" data-verdict={check.data.verdict}>
                {VERDICTS[check.data.verdict]}
              </span>
            </p>

            {check.data.findings.length === 0 ? (
              <p className="muted text-sm">Nichts zu beanstanden.</p>
            ) : (
              <ul className="findings">
                {check.data.findings.map((finding) => (
                  <li key={finding.code} data-severity={finding.severity}>
                    <strong>{finding.message}</strong>
                    <span className="text-sm">{finding.reason}</span>
                  </li>
                ))}
              </ul>
            )}

            <p className="muted text-sm">
              {check.data.stats.textLength} Zeichen Text · {check.data.stats.imageCount} Bilder ·{' '}
              {check.data.stats.linkCount} Links
              {check.data.stats.textToImageRatio !== null &&
                ` · ${check.data.stats.textToImageRatio} Zeichen je Bild`}
            </p>
          </>
        )}
      </div>
    </section>
  );
}

// --- DMARC-Berichte ---------------------------------------------------------

interface DmarcResult {
  totals: { messages: number; passed: number; failed: number };
  passRate: number | null;
  sources: Array<{ ip: string; count: number; aligned: boolean; dkim: string; spf: string }>;
  reports: Array<{ organisation: string; domain: string; policy: string }>;
  failures: string[];
}

function DmarcReports() {
  const [xml, setXml] = useState('');

  const parse = useMutation({
    mutationFn: () => api.post<DmarcResult>('/api/deliverability/dmarc', { reports: [xml] }),
  });

  return (
    <section className="card">
      <div className="card__body stack">
        <h2 style={{ fontSize: 'var(--text-lg)' }}>DMARC-Berichte</h2>
        <p className="muted text-sm">
          Die Berichte kommen täglich an die rua-Adresse und sind für Menschen unlesbar. Hier
          hineinkopiert beantworten sie die einzige Frage, die zählt: Wer versendet in deinem Namen
          — und besteht die Prüfung?
        </p>

        <div className="field">
          <label className="field__label" htmlFor="dmarc-xml">
            Bericht (XML)
          </label>
          <textarea
            id="dmarc-xml"
            className="input mono"
            rows={5}
            value={xml}
            onChange={(e) => setXml(e.target.value)}
            placeholder="<feedback>…</feedback>"
          />
        </div>

        <div>
          <Button
            onClick={() => parse.mutate()}
            disabled={xml.trim().length === 0}
            loading={parse.isPending}
          >
            Auswerten
          </Button>
        </div>

        {parse.isError && (
          <div className="alert" data-tone="danger" role="alert">
            {parse.error instanceof Error ? parse.error.message : 'Bericht nicht lesbar.'}
          </div>
        )}

        {parse.data && (
          <>
            <p>
              {parse.data.totals.messages.toLocaleString('de-CH')} Nachrichten ·{' '}
              {percent(parse.data.passRate)} bestanden · {parse.data.totals.failed} durchgefallen
            </p>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Absender-IP</th>
                    <th scope="col">Nachrichten</th>
                    <th scope="col">DKIM</th>
                    <th scope="col">SPF</th>
                    <th scope="col">DMARC</th>
                  </tr>
                </thead>
                <tbody>
                  {parse.data.sources.map((source) => (
                    <tr key={source.ip}>
                      <td className="mono">{source.ip}</td>
                      <td data-numeric>{source.count.toLocaleString('de-CH')}</td>
                      <td>{source.dkim}</td>
                      <td>{source.spf}</td>
                      <td>
                        <span className="badge" data-check={source.aligned ? 'ok' : 'fail'}>
                          {source.aligned ? 'bestanden' : 'durchgefallen'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted text-sm">
              DMARC gilt als bestanden, wenn DKIM <em>oder</em> SPF passt — Weiterleitungen brechen
              SPF regelmäßig, DKIM überlebt sie.
            </p>
          </>
        )}
      </div>
    </section>
  );
}
